/**
 * Cliente HTTP único de la aplicación: transporte, sesión y normalización de
 * errores.
 *
 * Toda petición al backend pasa por aquí. Es lo que permite que el manejo de
 * sesión expirada esté implementado UNA sola vez en vez de repartido por 45
 * servicios.
 *
 * ── Contrato: no lanza ───────────────────────────────────────────
 *
 * Ninguna función de este módulo lanza. Todo resultado —éxito, 4xx, 5xx, timeout
 * o caída de red— llega como un {@link HttpResult}. El llamador comprueba `ok`;
 * no envuelve en try/catch.
 *
 * ── Renovación de token y reintento ──────────────────────────────
 *
 * Ante un `401` se renueva el ID token de Firebase y se reintenta **una** vez. Si
 * el reintento vuelve a fallar con 401, la sesión ya no es recuperable y se cierra.
 *
 * Dos guardas evitan los fallos clásicos de este patrón:
 *  - `refreshInFlight` es un mutex de promesa: N peticiones que reciben 401 a la
 *    vez comparten UN solo refresco en lugar de disparar N contra Firebase.
 *  - `logoutDispatched` garantiza que el cierre de sesión y su notificación
 *    ocurran una única vez, aunque fallen varias peticiones en paralelo.
 *
 * Los handlers de refresco y logout se registran DESPUÉS de un login exitoso. Es
 * deliberado: durante `restaurarSesion()` (recarga de página) están en `null`,
 * de modo que ese flujo maneja sus propios errores sin desencadenar un logout en
 * cascada.
 *
 * ── Timeout ──────────────────────────────────────────────────────
 *
 * 90 segundos, vía `AbortController`. El mensaje de aborto advierte que el
 * servidor pudo haber procesado la operación de todos modos, porque en las
 * operaciones largas (generación de PDF, sincronización de formularios) el
 * timeout del cliente no cancela el trabajo del backend.
 *
 * ── Base de URL ──────────────────────────────────────────────────
 *
 * `VUE_APP_API_BASE_URL` se lee en tiempo de BUILD (Vue CLI). Si está vacía, las
 * rutas quedan relativas y las resuelve el proxy del dev server. Una ruta que ya
 * empiece por `http` se usa tal cual, sin anteponer la base.
 */

const API_BASE = process.env.VUE_APP_API_BASE_URL ?? ''

/**
 * Resultado uniforme de toda petición. Nunca se lanza una excepción: el fallo
 * viaja aquí dentro.
 *
 * Cómo distinguir los tres tipos de fallo por su `status`:
 *
 *   0    red caída o timeout — la petición no llegó a completarse
 *   401  sesión; ya gestionada por el interceptor antes de llegar al llamador
 *   422  validación; `errors` trae los mensajes POR CAMPO del backend
 *
 * En cualquier otro fallo, `errors` lleva la clave sintética `server` con un
 * único mensaje, de modo que el llamador siempre puede leerlo de la misma forma
 * (ver {@link primerError}).
 *
 * `data` solo está poblado cuando `ok` es `true`, y puede ser `undefined` aun así
 * si la respuesta no tenía cuerpo (por ejemplo un 204).
 *
 * @typeParam T - Forma del cuerpo de respuesta esperado.
 */
export interface HttpResult<T = unknown> {
  ok: boolean
  status: number
  data?: T
  errors?: Record<string, string[]>
}

// ─── Token de autorización ───────────────────────────────────────────────────

let authToken: string | null = null

/**
 * Fija el ID token que se enviará como `Authorization: Bearer` en cada petición.
 *
 * Lo llama el guard del router en cada navegación (con el token ya refrescado por
 * Firebase) y el store de sesión tras un login. Pasar `null` deja las peticiones
 * sin cabecera de autorización, que es lo que hace el logout.
 *
 * @param token - ID token de Firebase, o `null` para limpiarlo.
 */
export function setAuthToken(token: string | null): void {
  authToken = token
}

// ─── Handlers de errores de sesión ───────────────────────────────────────────
//
// Se registran DESPUÉS de un login exitoso, por lo que durante restaurarSesion()
// (page reload) son null y cada acción de auth maneja sus propios errores.
// Una vez activos, manejan automáticamente tokens expirados mid-sesión.

type RefreshFn = () => Promise<string | null>
type LogoutFn = () => void | Promise<void>

let refreshTokenFn: RefreshFn | null = null
let logoutFn: LogoutFn | null = null

// Singleton de refresco: si ya hay un refresh en curso, todas las peticiones 401
// concurrentes esperan la misma promesa en lugar de lanzar refreshes paralelos.
let refreshInFlight: Promise<string | null> | null = null
// Guard para que el logout y su notificación solo ocurran una vez.
let logoutDispatched = false

/**
 * Activa la recuperación automática de sesión ante un `401`.
 *
 * Debe llamarse SOLO tras un login exitoso. Mientras no se llame, un 401 se
 * devuelve tal cual al llamador sin intentar renovar ni cerrar sesión — que es
 * justo lo que necesita `restaurarSesion()` para manejar sus propios errores sin
 * provocar un logout en cascada durante la recarga de la página.
 *
 * Reinicia el guard de logout, de modo que una sesión nueva vuelve a tener
 * derecho a un cierre automático.
 *
 * @param refresh - Debe devolver un ID token fresco, o `null` si no puede.
 * @param logout - Se invoca una única vez cuando la sesión es irrecuperable.
 */
export function registrarHandlersAuth(refresh: RefreshFn, logout: LogoutFn): void {
  refreshTokenFn = refresh
  logoutFn = logout
  logoutDispatched = false
}

/**
 * Desactiva la recuperación automática de sesión y descarta cualquier refresco
 * en curso.
 *
 * Lo llama el logout. Descartar `refreshInFlight` importa: sin eso, un refresco
 * lanzado justo antes de cerrar sesión podría resolverse después y reinstalar un
 * token para una sesión que ya terminó.
 */
export function limpiarHandlersAuth(): void {
  refreshTokenFn = null
  logoutFn = null
  refreshInFlight = null
  logoutDispatched = false
}

/**
 * Token actualmente en uso, o `null` si no hay sesión.
 *
 * Para los pocos casos que necesitan la cabecera por su cuenta y no pueden pasar
 * por {@link http} — descargas de archivo abiertas en otra pestaña, por ejemplo.
 */
export function getAuthToken(): string | null {
  return authToken
}

// ─── Construcción de cabeceras ────────────────────────────────────────────────

function buildHeaders(hasBody: boolean): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (hasBody) headers['Content-Type'] = 'application/json'
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`
  return headers
}

// ─── Función central de petición ─────────────────────────────────────────────

const TIMEOUT_MS = 90_000

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  isRetry = false
): Promise<HttpResult<T>> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    const resp = await fetch(url, {
      method,
      headers: buildHeaders(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal
    })
    clearTimeout(timeoutId)

    const text = await resp.text()
    let parsed: unknown = undefined
    if (text) {
      try {
        parsed = JSON.parse(text)
      } catch {
        if (!resp.ok) {
          return {
            ok: false,
            status: resp.status,
            errors: { server: [`HTTP ${resp.status}: respuesta inesperada del servidor`] }
          }
        }
      }
    }

    if (!resp.ok) {
      // 401 → intentar renovar token Firebase y reintentar una sola vez.
      // Solo aplica si los handlers están registrados (es decir, sesión activa).
      if (resp.status === 401 && !isRetry && refreshTokenFn) {
        // Singleton: todas las peticiones 401 concurrentes comparten el mismo refresh.
        if (!refreshInFlight) {
          refreshInFlight = refreshTokenFn().finally(() => { refreshInFlight = null })
        }
        const newToken = await refreshInFlight
        if (newToken) {
          const retried = await request<T>(method, path, body, true)
          // Si el reintento también falla con 401, la sesión ya no es recuperable.
          if (!retried.ok && retried.status === 401 && !logoutDispatched) {
            logoutDispatched = true
            logoutFn?.()
          }
          return retried
        }
        // No se pudo obtener token fresco → cerrar sesión (solo una vez).
        if (!logoutDispatched) {
          logoutDispatched = true
          logoutFn?.()
        }
        return {
          ok: false,
          status: 401,
          errors: { server: ['Su sesión ha expirado. Por favor inicie sesión de nuevo.'] }
        }
      }

      const bodyObj = (parsed ?? {}) as { errors?: Record<string, string[]>; message?: string }
      return {
        ok: false,
        status: resp.status,
        errors: bodyObj.errors ?? { server: [bodyObj.message ?? `HTTP ${resp.status}`] }
      }
    }

    return { ok: true, status: resp.status, data: parsed as T }
  } catch (err: any) {
    clearTimeout(timeoutId)
    const msg = err?.name === 'AbortError'
      ? 'La solicitud tardó demasiado. El servidor puede haber procesado la operación.'
      : 'Error de red. Verifique su conexión.'
    return { ok: false, status: 0, errors: { network: [msg] } }
  }
}

// ─── API pública ──────────────────────────────────────────────────────────────

export const http = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path)
}

/** Extrae el primer mensaje de error legible de un HttpResult fallido. */
export function primerError(result: HttpResult, fallback = 'Ocurrió un error'): string {
  if (result.errors) {
    const first = Object.values(result.errors).flat()[0]
    if (first) return first
  }
  return fallback
}
